// =============================================
// healPendingLinks — post-publish heal dla markerów <span data-pending-link>
// =============================================
//
// Po publikacji noda X, scan wszystkich published wpisów w tej mapie szukając
// markerów `<span data-pending-link="/X.slug">anchor</span>`. Każdy znaleziony:
//   1. Replace na <a href="${X.wp_post_url}">anchor</a> w final_html
//   2. UPDATE thematic_node_content.final_html (DB source of truth)
//   3. wordpressService.updatePost(source.wp_post_id, { content: newHtml })
//      — TYLKO content field, non-destructive na title/date/featured_image
//
// Idempotent: heal matchuje TYLKO marker (data-pending-link), NIGDY generic <a>.
// Bezpieczny: per-mapId scoped, nie rusza wpisów spoza mapy.
//
// Używany w 2 miejscach:
// 1. publishQueueService.ts (STOP-3) — auto-trigger po successful publish (1 slug)
// 2. AuditPanel.tsx (STOP-4) — manual button "Re-heal all pending" per mapa

import { supabase } from "@/integrations/supabase/client";
import { wordpressService } from "@/services/wordpressService";
import { logPublic } from "@/services/contentEngine/contentLogger";
import { healPendingLinks } from "@/services/contentEngine/linkResolver";

interface WebsiteRow {
  id: string;
  url: string;
  login: string | null;
  password: string | null;
  api_key: string | null;
}

/**
 * Setup WP credentials dla danej mapy (z websites table).
 * Throw jeśli brak credentials — heal nie może zaktualizować WP.
 *
 * Exported — reused by applyLinkFixes.ts (STOP-2 link-audit).
 */
export async function setupWpForMap(mapId: string): Promise<WebsiteRow> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: mapRow } = await (supabase as any)
    .from("thematic_maps")
    .select("website_id")
    .eq("id", mapId)
    .maybeSingle();
  if (!mapRow?.website_id) {
    throw new Error(`Map ${mapId} ma brak website_id`);
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: website } = await (supabase as any)
    .from("websites")
    .select("id, url, login, password, api_key")
    .eq("id", mapRow.website_id)
    .maybeSingle();
  if (!website) throw new Error("Website nie znaleziony");
  const username = website.login;
  const password = website.api_key || website.password;
  if (!username || !password) {
    throw new Error("Brak WP credentials (login/api_key)");
  }
  wordpressService.setCredentials({
    url: String(website.url).replace(/\/$/, ""),
    username,
    password,
  });
  return website as WebsiteRow;
}

export interface HealOneResult {
  newlyPublishedNodeId: string;
  newlyPublishedSlug: string;
  wpUrl: string;
  /** Wpisy w mapie które miały marker do tego slug i zostały zaktualizowane. */
  sourcesUpdated: Array<{
    nodeId: string;
    wpPostId: number | null;
    healedCount: number;
    wpUpdateOk: boolean;
    wpUpdateError?: string;
  }>;
  /** Łączna liczba zhealowanych linków. */
  linksHealed: number;
}

/**
 * Heal markerów wskazujących na NOWO opublikowany node.
 *
 * @param newlyPublishedNodeId — UUID noda który właśnie wszedł live
 * @param mapId — scope (heal tylko w obrębie tej mapy)
 *
 * Wywoływane fire-and-forget z publishQueueService po successful publish.
 * Błąd loguje, nie throw'uje (publish jest już sukces, heal best-effort).
 */
export async function healPendingLinksForNewPublish(
  newlyPublishedNodeId: string,
  mapId: string,
): Promise<HealOneResult | null> {
  // 1. Pobierz slug + wp_post_url świeżo-opublikowanego noda
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: targetNode } = await (supabase as any)
    .from("thematic_nodes")
    .select("slug")
    .eq("id", newlyPublishedNodeId)
    .maybeSingle();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: targetContent } = await (supabase as any)
    .from("thematic_node_content")
    .select("wp_post_url")
    .eq("node_id", newlyPublishedNodeId)
    .maybeSingle();

  const slug = targetNode?.slug?.trim();
  const wpUrl = targetContent?.wp_post_url?.trim();
  if (!slug || !wpUrl) {
    logPublic(
      "HEAL",
      `Skip: node ${newlyPublishedNodeId.slice(0, 8)} brak slug lub wp_post_url`,
    );
    return null;
  }

  // 2. Query: wszystkie wpisy w tej mapie z markerem do /slug
  const marker = `data-pending-link="/${slug}"`;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: sources } = await (supabase as any)
    .from("thematic_node_content")
    .select("node_id, final_html, wp_post_id")
    .eq("map_id", mapId)
    .neq("node_id", newlyPublishedNodeId) // skip self
    .ilike("final_html", `%${marker}%`);

  if (!sources || sources.length === 0) {
    return {
      newlyPublishedNodeId,
      newlyPublishedSlug: slug,
      wpUrl,
      sourcesUpdated: [],
      linksHealed: 0,
    };
  }

  // 3. Setup WP credentials (jeden raz dla wszystkich update)
  try {
    await setupWpForMap(mapId);
  } catch (e) {
    logPublic(
      "HEAL",
      `WP setup failed dla mapy ${mapId}: ${e instanceof Error ? e.message : String(e)}`,
    );
    return {
      newlyPublishedNodeId,
      newlyPublishedSlug: slug,
      wpUrl,
      sourcesUpdated: [],
      linksHealed: 0,
    };
  }

  // 4. Dla każdego source: heal HTML → update DB → update WP
  const resolutions = new Map<string, string>([[slug, wpUrl]]);
  const sourcesUpdated: HealOneResult["sourcesUpdated"] = [];
  let linksHealed = 0;

  for (const source of sources as Array<{
    node_id: string;
    final_html: string;
    wp_post_id: number | null;
  }>) {
    const r = healPendingLinks(source.final_html, resolutions);
    if (r.healed.length === 0) {
      // Marker był ale regex nie matched (corrupted HTML?) — skip
      continue;
    }

    // Update DB
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error: dbErr } = await (supabase as any)
      .from("thematic_node_content")
      .update({ final_html: r.html })
      .eq("node_id", source.node_id);
    if (dbErr) {
      logPublic(
        "HEAL",
        `DB update failed dla source ${source.node_id.slice(0, 8)}: ${dbErr.message}`,
      );
      continue;
    }

    // Update WP — TYLKO content field, non-destructive
    let wpUpdateOk = false;
    let wpUpdateError: string | undefined;
    if (source.wp_post_id) {
      try {
        const updRes = await wordpressService.updatePost(source.wp_post_id, {
          content: r.html,
        });
        wpUpdateOk = !!updRes.success;
        if (!wpUpdateOk) {
          wpUpdateError = updRes.error || "unknown WP error";
        }
      } catch (e) {
        wpUpdateError = e instanceof Error ? e.message : String(e);
      }
    } else {
      wpUpdateError = "wp_post_id is null — DB updated but WP not";
    }

    sourcesUpdated.push({
      nodeId: source.node_id,
      wpPostId: source.wp_post_id,
      healedCount: r.healed.length,
      wpUpdateOk,
      wpUpdateError,
    });
    linksHealed += r.healed.length;

    logPublic(
      "HEAL",
      `Source ${source.node_id.slice(0, 8)} → ${r.healed.length} linki do /${slug} ${wpUpdateOk ? "✓ WP updated" : `✗ WP error: ${wpUpdateError}`}`,
    );
  }

  return {
    newlyPublishedNodeId,
    newlyPublishedSlug: slug,
    wpUrl,
    sourcesUpdated,
    linksHealed,
  };
}

export interface HealAllResult {
  mapId: string;
  scanned: number; // wpisy w mapie z markerami
  healed: number; // wpisy gdzie cokolwiek zhealowano
  linksHealed: number; // total liczba zhealowanych linków
  stillPending: number; // markery które NIE mają target wp_post_url
  errors: Array<{ nodeId: string; error: string }>;
}

/**
 * Manual heal dla całej mapy (STOP-4 button "Re-heal pending").
 *
 * Buduje pełną resolutions map z aktualnego stanu DB (wszystkie published nody
 * w mapie) i przepuszcza ją przez heal dla każdego wpisu z markerami.
 *
 * Opt-in only — wywoływany manualnie z UI. Per-mapa scoped.
 */
export async function healAllPendingForMap(
  mapId: string,
): Promise<HealAllResult> {
  const result: HealAllResult = {
    mapId,
    scanned: 0,
    healed: 0,
    linksHealed: 0,
    stillPending: 0,
    errors: [],
  };

  // 1. Pobierz wszystkie nody w mapie + wp_post_url (resolutions)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: nodes } = await (supabase as any)
    .from("thematic_nodes")
    .select("id, slug")
    .eq("map_id", mapId);
  if (!nodes || nodes.length === 0) return result;

  const nodeIds = (nodes as Array<{ id: string; slug: string }>).map(
    (n) => n.id,
  );
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: contents } = await (supabase as any)
    .from("thematic_node_content")
    .select("node_id, wp_post_url, final_html, wp_post_id")
    .in("node_id", nodeIds);

  const wpUrlByNodeId = new Map<string, string | null>();
  for (const c of (contents || []) as Array<{
    node_id: string;
    wp_post_url: string | null;
  }>) {
    wpUrlByNodeId.set(c.node_id, c.wp_post_url?.trim() || null);
  }

  const resolutions = new Map<string, string>();
  for (const n of nodes as Array<{ id: string; slug: string }>) {
    const wpUrl = wpUrlByNodeId.get(n.id);
    if (n.slug && wpUrl) {
      resolutions.set(n.slug, wpUrl);
    }
  }

  // 2. Setup WP credentials
  try {
    await setupWpForMap(mapId);
  } catch (e) {
    result.errors.push({
      nodeId: "_setup",
      error: e instanceof Error ? e.message : String(e),
    });
    return result;
  }

  // 3. Scan wpisów z markerami
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: candidates } = await (supabase as any)
    .from("thematic_node_content")
    .select("node_id, final_html, wp_post_id")
    .eq("map_id", mapId)
    .ilike("final_html", "%data-pending-link=%");

  if (!candidates || candidates.length === 0) {
    return result;
  }

  // 4. Heal każdego kandydata
  for (const c of candidates as Array<{
    node_id: string;
    final_html: string;
    wp_post_id: number | null;
  }>) {
    result.scanned++;
    const r = healPendingLinks(c.final_html, resolutions);

    if (r.healed.length === 0) {
      // Brak resolutions dla żadnego markera w tym wpisie — zostają jako pending
      // Count still-pending markers
      const remaining = (
        c.final_html.match(/data-pending-link="\/[^"]+"/g) || []
      ).length;
      result.stillPending += remaining;
      continue;
    }

    // Update DB
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error: dbErr } = await (supabase as any)
      .from("thematic_node_content")
      .update({ final_html: r.html })
      .eq("node_id", c.node_id);
    if (dbErr) {
      result.errors.push({
        nodeId: c.node_id,
        error: `DB update: ${dbErr.message}`,
      });
      continue;
    }

    // Update WP
    if (c.wp_post_id) {
      try {
        const updRes = await wordpressService.updatePost(c.wp_post_id, {
          content: r.html,
        });
        if (!updRes.success) {
          result.errors.push({
            nodeId: c.node_id,
            error: `WP update: ${updRes.error || "unknown"}`,
          });
          continue;
        }
      } catch (e) {
        result.errors.push({
          nodeId: c.node_id,
          error: `WP update threw: ${e instanceof Error ? e.message : String(e)}`,
        });
        continue;
      }
    }

    result.healed++;
    result.linksHealed += r.healed.length;

    // Policz remaining still-pending dla tego wpisu po heal
    const remainingAfter = (
      r.html.match(/data-pending-link="\/[^"]+"/g) || []
    ).length;
    result.stillPending += remainingAfter;
  }

  return result;
}
